import { useMemo, useState } from 'react';

import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { usePermissions } from '@/shared/permissions';
import { useTenant } from '@/shared/tenant/useTenant';
import { EmptyState, SectionScaffold } from '@/shared/ui';

/**
 * Orders section — the order-management workspace for the active tenant.
 *
 * The order list (search + table) will render here once the OMS data API is
 * wired; today it surfaces the configuration entry point. Order lifecycle and
 * OMS behaviour are configured behind the header gear.
 */
export function OrdersPage() {
  const { hasPermission } = usePermissions();
  const { tenantName } = useTenant();
  const [search, setSearch] = useState('');

  const canView = hasPermission('settings.orders.view');
  const tenantLabel = useMemo(() => tenantName ?? 'this store', [tenantName]);

  return (
    <SectionScaffold
      title="Orders"
      subtitle="Track, search and fulfil customer orders for this store."
      canView={canView}
      settingsTo="/settings/orders"
      settingsLabel="Order settings"
      search={{
        value: search,
        onChange: setSearch,
        placeholder: 'Search orders by number or customer…',
        disabled: true,
        disabledHint: 'Order search becomes available once the order service is connected.',
      }}
    >
      <EmptyState
        icon={<ReceiptLongRoundedIcon sx={{ fontSize: 48 }} />}
        title="Order management is coming soon"
        description={`Incoming orders for ${tenantLabel} will appear here. In the meantime, configure the OMS mode, auto-cancel, confirmation and lifecycle transitions.`}
        action={
          <Button
            variant="outlined"
            component={RouterLink}
            to="/settings/orders"
            startIcon={<SettingsRoundedIcon />}
          >
            Open order settings
          </Button>
        }
      />
    </SectionScaffold>
  );
}
